import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Pickaxe,
  Lock,
  Eye,
  Network,
  ShieldCheck,
  ClipboardCheck,
  Users,
  MessagesSquare,
  FileSearch,
  Layers,
  Scale,
  FileText,
  Gem,
  MapPin,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { colors } from '../styles/colors';
import { useFillout } from '../hooks/useFillout';

const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{eyebrow}</span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

const MinesPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const navigate = useNavigate();
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  // §4 framework flow
  const frameworkSteps = [
    { step: '01', title: 'Project Review', body: 'Technical review and eligibility assessment.', Icon: FileSearch },
    { step: '02', title: 'Reserve Participation', body: 'Approved projects enter the reserve framework.', Icon: Layers },
    { step: '03', title: 'Reserve Visibility', body: 'Assets become visible within the reserve environment.', Icon: Eye },
    { step: '04', title: 'Liquidity Access', body: 'Structured participation may create liquidity opportunities.', Icon: Network },
  ];

  // §5 benefits
  const benefits = [
    { Icon: Lock, title: 'Retain Ownership', body: 'Maintain control of the underlying project.' },
    { Icon: Eye, title: 'Reserve Visibility', body: 'Participate within a transparent reserve framework.' },
    { Icon: Network, title: 'Access Liquidity', body: 'Create new pathways between verified resources and capital.' },
    { Icon: ClipboardCheck, title: 'Independent Review', body: 'Support participation through technical assessment and verification.' },
    { Icon: Users, title: 'Ecosystem Participation', body: 'Connect with liquidity providers through a structured framework.' },
  ];

  // §6 suitability criteria
  const suitability = [
    { Icon: Gem, label: 'Resource Quality' },
    { Icon: FileText, label: 'Technical Documentation' },
    { Icon: MapPin, label: 'Jurisdiction' },
    { Icon: TrendingUp, label: 'Project Maturity' },
    { Icon: ShieldCheck, label: 'Verification Standards' },
    { Icon: Layers, label: 'Reserve Suitability' },
  ];

  // §8 governance
  const governance = [
    { Icon: ClipboardCheck, title: 'Technical Review', body: 'Independent technical assessment of resource projects.' },
    { Icon: ShieldCheck, title: 'Independent Validation', body: 'Verification standards supporting reserve integrity.' },
    { Icon: Scale, title: 'Governance Separation', body: 'Corporate, reserve, and marketplace layers operate distinctly.' },
    { Icon: FileText, title: 'Reserve Reporting', body: 'Ongoing visibility through reserve composition and reporting.' },
    { Icon: Users, title: 'Ecosystem Partners', body: 'Defined participant roles across the broader ecosystem.' },
  ];

  // §9 engagement
  const engagementSteps = [
    { step: '01', title: 'Initial Discussion', body: 'Understand the project and objectives.', Icon: MessagesSquare },
    { step: '02', title: 'Eligibility Review', body: 'Assess documentation and project suitability.', Icon: FileSearch },
    { step: '03', title: 'Reserve Assessment', body: 'Review reserve participation opportunities.', Icon: Layers },
    { step: '04', title: 'Ongoing Engagement', body: 'Maintain communication, reporting, and reserve visibility.', Icon: FileText },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* §1 HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: '92vh', background: 'linear-gradient(180deg, #000000 0%, #03090C 60%, #07141A 100%)' }}>
        <picture>
          <source srcSet="/mines-hero.webp" type="image/webp" />
          <img
            src="/mines-hero.jpg"
            alt=""
            aria-hidden="true"
            data-testid="mines-hero-image"
            className={`absolute inset-0 w-full h-full object-cover mines-hero-drift transition-opacity duration-[2000ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ objectPosition: 'center center' }}
            loading="eager"
            decoding="async"
          />
        </picture>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.28) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.92) 100%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(55% 60% at 50% 55%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[92vh] pt-32 pb-20 sm:pt-36 sm:pb-24">
          <div className={`transition-all duration-1000 ease-out transform ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}>
            <div className="flex items-center gap-3 mb-7 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-medium" style={{ color: colors.teal, fontFamily: 'inherit' }}>
                <Pickaxe className="w-3.5 h-3.5" strokeWidth={1.5} />
                For Mining Projects
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-[-0.02em] mb-7 max-w-4xl mx-auto" style={{ color: colors.white, fontFamily: 'inherit', fontWeight: 700 }}>
              ACCESS LIQUIDITY WITHOUT SURRENDERING OWNERSHIP.
            </h1>
            <p className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-2xl mx-auto mb-12" style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'inherit' }}>
              Arizore helps connect verified mineral resources with liquidity opportunities through a transparent reserve framework.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={openFilloutPopup}
                data-testid="mines-hero-discuss-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}`, fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Discuss Your Project
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/reserve-architecture')}
                data-testid="mines-hero-framework-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)', fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              >
                Understand The Reserve Framework
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes mines-hero-drift {
            0% { transform: scale(1.04) translate3d(0,0,0); }
            50% { transform: scale(1.06) translate3d(-0.5%, -0.5%, 0); }
            100% { transform: scale(1.04) translate3d(0,0,0); }
          }
          .mines-hero-drift { animation: mines-hero-drift 24s ease-in-out infinite; will-change: transform; }
          @media (prefers-reduced-motion: reduce) { .mines-hero-drift { animation: none; } }
        `}</style>
      </section>

      {/* §2 CAPITAL ACCESS CHALLENGE */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/home-bg-grid.webp)', backgroundSize: 'cover', backgroundPosition: 'center 20%', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.65) 50%, #040404 100%)' }} />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Capital Access Challenge"
            title="VALUABLE RESOURCES DO NOT ALWAYS MEAN ACCESSIBLE CAPITAL."
            sub="Mining projects often hold substantial resource value while facing significant barriers to capital. Traditional funding options may involve trade-offs that limit operational flexibility."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {['Equity dilution', 'Lengthy approval processes', 'Restrictive financing terms', 'Reduced operational flexibility'].map((point, idx) => (
              <div
                key={point}
                data-testid={`mines-challenge-${idx + 1}`}
                className="p-5 sm:p-6 rounded-xl text-center"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="mx-auto w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(242,214,117,0.07)', border: '1px solid rgba(242,214,117,0.22)' }}>
                  <span className="text-xs font-semibold tracking-[0.12em]" style={{ color: colors.goldMid }}>{String(idx + 1).padStart(2, '0')}</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.75 }}>{point}</p>
              </div>
            ))}
          </div>

          <p className="max-w-2xl mx-auto text-center text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.75 }}>
            The challenge is not always the asset. The challenge is creating{' '}
            <span style={{ color: colors.teal, fontWeight: 500 }}>trusted pathways between verified resources and available liquidity</span>.
          </p>
        </div>
      </section>

      {/* §3 WHY ARIZORE EXISTS */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.14 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.55) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Arizore Exists"
            title="CONNECTING RESOURCES AND LIQUIDITY."
            sub="Arizore was created to help bridge the gap between verified resources and capital participation. Through the reserve framework, eligible projects may participate in an ecosystem designed around transparency, verification, reserve visibility, and liquidity access."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {[
              { Icon: ShieldCheck, label: 'Verified resources enter a structured framework' },
              { Icon: Lock, label: 'Ownership remains with the operator' },
              { Icon: Eye, label: 'Reserve participation creates visibility' },
              { Icon: Sparkles, label: 'Liquidity providers gain greater transparency' },
              { Icon: Scale, label: 'Governance and reporting support trust' },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`mines-why-${label.toLowerCase().slice(0, 20).replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                className="flex flex-col items-center text-center p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-xs leading-snug" style={{ color: colors.white, opacity: 0.85 }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §4 RESERVE FRAMEWORK FLOW */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-6 sm:px-10 lg:px-16 py-14 sm:py-16 lg:py-20"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <SectionHeading
              eyebrow="How The Reserve Framework Works"
              title="A STRUCTURED FRAMEWORK FOR PARTICIPATION."
              sub="The reserve framework creates a structured environment that connects resources, reserve visibility, and liquidity providers."
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-12">
              {frameworkSteps.map(({ step, title, body, Icon }, idx) => (
                <div
                  key={step}
                  data-testid={`mines-framework-step-${step}`}
                  className="relative p-6 sm:p-7 rounded-2xl"
                  style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${colors.teal}24` }}
                >
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#070707', color: colors.teal, border: `1px solid ${colors.teal}80` }}>
                    {step}
                  </span>
                  <div className="mt-3 mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                    <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.12em] mb-2 text-center" style={{ color: colors.white }}>{title}</h3>
                  <p className="text-sm leading-relaxed text-center" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
                  {idx < 3 && (
                    <ArrowRight aria-hidden="true" className="hidden lg:block absolute top-1/2 -right-4 w-5 h-5 -translate-y-1/2" style={{ color: colors.teal, opacity: 0.4 }} strokeWidth={1.5} />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => navigate('/reserve-architecture')}
                data-testid="mines-framework-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                Explore Reserve Architecture
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* §5 WHY PROJECTS PARTICIPATE */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.6) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Why Projects Participate" title="BUILT AROUND RESOURCE OWNERS." sub="The reserve framework is designed to respect ownership while creating new pathways for verified resources." />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {benefits.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`mines-benefit-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group p-7 sm:p-8 rounded-2xl h-full flex flex-col transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}40`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 20px 40px -20px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 leading-tight tracking-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §6 WHAT MAKES A PROJECT SUITABLE */}
      <section className="py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="What Makes A Project Suitable"
            title="NOT EVERY RESOURCE PROJECT IS THE SAME."
            sub="Participation depends on asset quality, documentation standards, reserve suitability, and technical review outcomes. The following are review criteria, not guarantees of eligibility."
          />

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-10">
            {suitability.map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`mines-suitability-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex flex-col items-center text-center p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.12em] leading-tight" style={{ color: colors.white }}>{label}</span>
              </div>
            ))}
          </div>

          <p className="max-w-2xl mx-auto text-center text-xs sm:text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.45 }}>
            Review criteria are indicative only. Final participation depends on independent assessment and reserve framework requirements.
          </p>
        </div>
      </section>

      {/* §8 TECHNICAL REVIEW & GOVERNANCE */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.6) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Technical Review & Governance"
            title="STRUCTURE BUILDS CREDIBILITY."
            sub="Participation is supported by technical review processes, governance standards, reserve reporting, and ecosystem partners."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-12">
            {governance.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`mines-governance-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.12em] mb-2 leading-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-xs sm:text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => navigate('/reserve-data/azg')}
              data-testid="mines-governance-cta-reserve-data"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              View Reserve Data
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* §9 HOW ENGAGEMENT WORKS */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="How Engagement Works"
            title="A STRUCTURED REVIEW PROCESS."
            sub="Engagement follows a deliberate, consultative sequence. This is a review process, not a funding application."
          />

          <ol className="relative space-y-3">
            <div aria-hidden="true" className="absolute left-7 sm:left-8 top-7 bottom-7 w-px" style={{ background: `linear-gradient(180deg, transparent 0%, ${colors.teal}50 8%, ${colors.teal}50 92%, transparent 100%)` }} />
            {engagementSteps.map(({ step, title, body, Icon }) => (
              <li
                key={step}
                data-testid={`mines-engagement-step-${step}`}
                className="relative flex items-start gap-5 sm:gap-6 p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="relative z-10 flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#070707', border: `1px solid ${colors.teal}50`, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.03), 0 0 0 4px rgba(4,4,4,1)' }}>
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <div className="flex-grow pt-1">
                  <span className="block text-[10px] uppercase tracking-[0.28em] font-medium mb-1.5" style={{ color: colors.teal, opacity: 0.85 }}>Step {step}</span>
                  <h3 className="text-base sm:text-lg font-semibold tracking-tight mb-2" style={{ color: colors.white }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* §10 GET IN TOUCH */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.09 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.75) 50%, #040404 100%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Get In Touch"
            title="DISCUSS YOUR PROJECT."
            sub="If you represent a mining project seeking access to liquidity through a transparent reserve framework, speak with the Arizore team."
          />
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={openFilloutPopup}
              data-testid="mines-contact-discuss-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Discuss Your Project
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/reserve-architecture')}
              data-testid="mines-contact-framework-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            >
              Understand The Reserve Framework
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MinesPage;
